/**
 * Event Bus for global Vue component communication.
 *
 * Centralized pub/sub bus for cross-component events that don't fit a
 * parent/child relationship. Naming convention: dot-separated lowercase
 * (e.g. `permissions.ready`, `modal.cliente.open`, `filter.applied`).
 */

import { log } from './utils/DebugLogger'

export type EventCallback<T = unknown> = (data?: T) => void

const debug = true

class EventBus {
  private events: Record<string, EventCallback[]> = {}

  on<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (debug) {
      log('EventBus', 'on', 'Register listener for', event)
    }

    if (event.includes('modal') || event.includes('contato-fidelizacao') || event.includes('payment-link')) {
      log('EventBus', 'on', 'Registering listener for event:', event, 'callback:', callback.name || 'anonymous')
    }

    if (!this.events[event]) {
      this.events[event] = []
    }
    this.events[event].push(callback as EventCallback)

    if (event.includes('modal') || event.includes('contato-fidelizacao') || event.includes('payment-link')) {
      log('EventBus', 'on', 'Total listeners for', event, ':', this.events[event].length)
    }
  }

  off<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (debug) {
      log('EventBus', 'off', 'Remove listener for', event)
    }
    if (this.events[event]) {
      this.events[event] = this.events[event].filter((cb) => cb !== callback)
    }
  }

  emit<T = unknown>(event: string, data?: T): void {
    if (debug) {
      log('EventBus', 'emit', 'Emit event', event, data)
    }

    if (event.includes('modal') || event.includes('contato-fidelizacao')) {
      log('EventBus', 'emit', 'Emitting event:', event, 'with data:', data)
      log('EventBus', 'emit', 'Listeners count:', this.events[event] ? this.events[event].length : 0)
    }

    if (this.events[event]) {
      if (event.includes('payment-link')) {
        log('EventBus', 'emit', `CALLING ${this.events[event].length} listeners for event:`, event)
      }
      this.events[event].forEach((callback, index) => {
        try {
          if (event.includes('modal') || event.includes('contato-fidelizacao') || event.includes('payment-link')) {
            log(
              'EventBus',
              'emit',
              `Calling listener #${index + 1} for event:`,
              event,
              'callback:',
              callback.name || 'anonymous'
            )
          }
          callback(data)
        } catch (err) {
          console.error(`Error in event listener for "${event}":`, err)
        }
      })
    } else if (event.includes('modal') || event.includes('contato-fidelizacao') || event.includes('payment-link')) {
      console.warn('⚠️ [EventBus] No listeners registered for event:', event)
    }
  }

  once<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (debug) {
      log('EventBus', 'once', 'Register once listener for', event)
    }
    const onceCallback: EventCallback<T> = (data) => {
      callback(data)
      this.off(event, onceCallback)
    }
    this.on(event, onceCallback)
  }

  removeAllListeners(event: string): void {
    if (debug) {
      log('EventBus', 'removeAllListeners', 'Remove all listeners for', event)
    }
    if (this.events[event]) {
      this.events[event] = []
    }
  }

  getEvents(): string[] {
    if (debug) {
      log('EventBus', 'getEvents', 'Get events')
    }
    return Object.keys(this.events)
  }

  getListenerCount(event: string): number {
    if (debug) {
      log('EventBus', 'getListenerCount', 'Get listener count for', event)
    }
    return this.events[event] ? this.events[event].length : 0
  }
}

export const eventBus = new EventBus()
export { EventBus }
